/**
 * In-app notification system using Firestore real-time listeners
 * Works without browser permission - complements FCM for better UX
 *
 * Usage:
 * - Always shows in-app notifications (toast) when app is open
 * - Works in emulator mode perfectly
 * - Provides notification history
 * - FCM still handles background notifications when user grants permission
 */

import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  writeBatch,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';

export interface InAppNotification {
  id: string;
  type: 'like' | 'comment' | 'follow' | 'lesson_request' | 'lesson_accepted' | 'lesson_completed' | 'message';
  title: string;
  message: string;
  link?: string;        // Primary — points to the specific message in the conversation view
  fallbackLink?: string; // Fallback — lesson/orders page if primary target is unavailable
  read: boolean;
  createdAt: Timestamp;
  data?: Record<string, unknown>;
}

type NotificationCallback = (notification: InAppNotification) => void;

/**
 * Subscribe to real-time notifications for a user
 * Shows toast notifications when new notifications arrive
 * Returns unsubscribe function
 *
 * @example
 * const unsubscribe = subscribeToNotifications(
 *   currentUser.uid,
 *   (notification) => {
 *     showToast(notification.message, 'info');
 *   }
 * );
 *
 * // Later, cleanup:
 * unsubscribe();
 */
export function subscribeToNotifications(
  userId: string,
  onNewNotification: NotificationCallback,
  onError?: (error: Error) => void
): () => void {
  if (!userId) {
    console.warn('subscribeToNotifications called without userId');
    return () => {};
  }

  try {
    const notificationsRef = collection(db, 'users', userId, 'notifications');
    const q = query(
      notificationsRef,
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    // Track what we've already seen to only show new ones
    let isFirstLoad = true;

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          // Only show notifications for new additions (not on initial load)
          if (change.type === 'added' && !isFirstLoad) {
            const notification: InAppNotification = {
              id: change.doc.id,
              ...change.doc.data(),
            } as InAppNotification;

            // Call the callback with the new notification
            onNewNotification(notification);
          }
        });

        // After first load, start showing new notifications
        if (isFirstLoad) {
          isFirstLoad = false;
        }
      },
      (error) => {
        console.error('Error listening to notifications:', error);
        console.error('Error code:', (error as { code?: string })?.code);
        console.error('Error details:', {
          userId,
          error: error instanceof Error ? error.message : String(error),
        });
        if (onError) onError(error);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up notification listener:', error);
    if (onError) onError(error as Error);
    return () => {}; // Return no-op unsubscribe
  }
}

/**
 * Get unread notification count
 */
export function subscribeToUnreadCount(
  userId: string,
  onCountChange: (count: number) => void,
  onError?: (error: Error) => void
): () => void {
  try {
    const notificationsRef = collection(db, 'users', userId, 'notifications');
    const q = query(
      notificationsRef,
      where('read', '==', false)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        onCountChange(snapshot.size);
      },
      (error) => {
        console.error('Error listening to unread count:', error);
        if (onError) onError(error);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up unread count listener:', error);
    if (onError) onError(error as Error);
    return () => {};
  }
}

/**
 * Mark a notification as read
 */
export async function markNotificationAsRead(userId: string, notificationId: string): Promise<void> {
  const notificationRef = doc(db, 'users', userId, 'notifications', notificationId);
  await updateDoc(notificationRef, {
    read: true,
    readAt: new Date().toISOString(),
  });
}

/**
 * Mark all notifications as read
 */
export async function markAllNotificationsAsRead(userId: string): Promise<void> {
  const notificationsRef = collection(db, 'users', userId, 'notifications');
  const q = query(
    notificationsRef,
    where('read', '==', false),
    limit(50)
  );

  const snapshot = await getDocs(q);
  const batch = writeBatch(db);

  snapshot.docs.forEach((docSnap) => {
    batch.update(docSnap.ref, {
      read: true,
      readAt: new Date().toISOString(),
    });
  });

  await batch.commit();
}

/**
 * Subscribe to unread notifications only (for notification center)
 */
export function subscribeToUnreadNotifications(
  userId: string,
  onNotificationsChange: (notifications: InAppNotification[]) => void,
  onError?: (error: Error) => void
): () => void {
  try {
    const notificationsRef = collection(db, 'users', userId, 'notifications');
    const q = query(
      notificationsRef,
      where('read', '==', false),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const notifications = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as InAppNotification[];
        onNotificationsChange(notifications);
      },
      (error) => {
        console.error('Error listening to unread notifications:', error);
        if (onError) onError(error);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up unread notifications listener:', error);
    if (onError) onError(error as Error);
    return () => {};
  }
}

/**
 * Get all notifications (for notification center)
 */
export async function getNotifications(
  userId: string,
  limitCount: number = 50
): Promise<InAppNotification[]> {
  const notificationsRef = collection(db, 'users', userId, 'notifications');
  const q = query(
    notificationsRef,
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as InAppNotification[];
}
